import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { matterApi, http, type MatterSummary, matterDisplayName } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import Icon from '../components/common/Icon';

type Task = { ROWID: number; matter_id: number; name: string; status: string; priority?: string; due_date?: string | null };
type DiaryEvent = { ROWID: number; matter_id: number | null; kind: string; title: string; starts_at: string; severity?: string };
type UnbilledSummary = {
  matters_with_unbilled: number;
  unbilled_hours: number;
  unbilled_fee_zar: number;
  unbilled_disbursements_zar: number;
  unbilled_total_zar: number;
};

export default function Dashboard() {
  const { user } = useAuth();
  const { active } = useTenant();
  const [matters, setMatters] = useState<MatterSummary[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [diary, setDiary] = useState<DiaryEvent[]>([]);
  const [wip, setWip] = useState<UnbilledSummary | null>(null);

  useEffect(() => {
    if (!active) return;
    matterApi.list({ status: 'open' }).then((r) => setMatters(r.matters.slice(0, 10))).catch(() => null);
    http.get<{ tasks: Task[] }>('/tasks?status=open').then((r) => setTasks(r.tasks.slice(0, 10))).catch(() => null);
    const from = new Date().toISOString().slice(0, 10);
    const to = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    http.get<{ events: DiaryEvent[] }>(`/diary?from=${from}&to=${to}`).then((r) => setDiary(r.events.slice(0, 10))).catch(() => null);
    http.get<UnbilledSummary>('/invoices/unbilled-summary').then(setWip).catch(() => null);
  }, [active]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const firstName = user?.first_name || user?.display_name?.split(' ')[0] || user?.email?.split('@')[0] || '';

  return (
    <div className="stack">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
        <div>
          <h1 style={{ fontSize: 28 }}>{greeting}{firstName ? `, ${firstName}` : ''}</h1>
          <p className="muted">Here's what's on your plate today.</p>
        </div>
        <Link to="/matters/new"><button className="primary">+ New matter</button></Link>
      </div>

      {active && !active.onboarding_complete && (
        <div className="card" style={{ borderColor: 'var(--color-accent)', background: 'rgba(201,163,74,0.08)' }}>
          <div className="row">
            <div className="grow">
              <b>Finish setting up {active.tenant_name}</b>
              <p className="muted" style={{ marginTop: 4 }}>Complete the onboarding wizard for full access.</p>
            </div>
            <Link to="/onboarding"><button className="primary">Continue</button></Link>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
        <MetricCard label="Open matters" count={matters.length} icon="briefcase" to="/matters" />
        <MetricCard label="Open tasks"   count={tasks.length}   icon="check-list" to="/tasks" />
        <MetricCard label="Upcoming diary (14d)" count={diary.length} icon="calendar" to="/diary" />
        <MetricCard
          label="Unbilled WIP"
          count={wip ? formatZarShort(wip.unbilled_total_zar) : '—'}
          hint={wip ? `${wip.unbilled_hours.toFixed(1)}h · ${wip.matters_with_unbilled} matter${wip.matters_with_unbilled === 1 ? '' : 's'}` : undefined}
          icon="receipt"
          to="/fee-book"
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 14 }}>
        <div className="card">
          <div className="row" style={{ marginBottom: 10 }}>
            <h3 className="grow" style={{ margin: 0 }}>Open matters</h3>
            <Link to="/matters"><button>See all →</button></Link>
          </div>
          {matters.length === 0 ? (
            <p className="muted">No open matters yet. <Link to="/matters/new">Create your first matter</Link>.</p>
          ) : (
            <table>
              <thead><tr><th>Reference</th><th>Name</th><th>Case no.</th><th>Opened</th></tr></thead>
              <tbody>
                {matters.map((m) => (
                  <tr key={m.ROWID}>
                    <td><Link to={`/matters/${m.ROWID}`}>{m.reference}</Link></td>
                    <td>{matterDisplayName(m)}</td>
                    <td>{m.case_number || '—'}</td>
                    <td>{m.opened_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card">
          <div className="row" style={{ marginBottom: 10 }}>
            <h3 className="grow" style={{ margin: 0 }}>Upcoming</h3>
            <Link to="/diary"><button>Full diary →</button></Link>
          </div>
          {diary.length === 0 ? (
            <p className="muted">No diary events in the next 14 days.</p>
          ) : (
            <div className="stack">
              {diary.map((d) => (
                <div key={d.ROWID} className="row" style={{ gap: 10 }}>
                  <div style={{
                    width: 4, alignSelf: 'stretch',
                    background: d.severity === 'critical' || d.severity === 'statutory' ? 'var(--color-danger)' : 'var(--color-accent)',
                    borderRadius: 2,
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.title}</div>
                    <div className="muted" style={{ fontSize: 12 }}>{formatDate(d.starts_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, count, icon, to, hint }: { label: string; count: number | string; icon: Parameters<typeof Icon>[0]['name']; to: string; hint?: string }) {
  return (
    <Link to={to} style={{ textDecoration: 'none' }}>
      <div className="card">
        <div className="row" style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>
          <Icon name={icon} size={14} /><span style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
        </div>
        <div style={{ fontSize: 36, fontWeight: 700, marginTop: 8, color: 'var(--color-text)' }}>{count}</div>
        {hint && <div className="muted" style={{ fontSize: 11 }}>{hint}</div>}
      </div>
    </Link>
  );
}

function formatZarShort(n: number) {
  if (!n) return 'R 0';
  if (n >= 1_000_000) return `R ${(n / 1_000_000).toFixed(1)}m`;
  if (n >= 1_000)     return `R ${(n / 1_000).toFixed(1)}k`;
  return `R ${n.toFixed(0)}`;
}

function formatDate(iso: string) {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}
